import { DateTime } from "luxon";
import { useEffect } from "react";
import { useConfig } from "@/hooks/use-config";
import { useCalendarStateStore } from "../store/use-calendar-state-store";
import type { CalendarViewType } from "../types";
import { DayView } from "./views/day-view";
import { MonthView } from "./views/month-view";
import { ThreeDayView } from "./views/three-day-view";
import { WeekView } from "./views/week-view";
import { WorkerView } from "./views/worker-view";

interface CalendarViewRendererProps {
  viewType: CalendarViewType;
  currentDate: DateTime;
  onDateChange?: (date: DateTime) => void;
  onAppointmentClick?: (appointmentId: string) => void;
  onAppointmentDrop?: (
    appointmentId: string,
    newDate: DateTime,
    newWorkerId?: string | null
  ) => void;
}

export function CalendarViewRenderer({
  viewType,
  currentDate,
  onDateChange,
  onAppointmentClick,
  onAppointmentDrop,
}: CalendarViewRendererProps) {
  const { value: showWorkerAssignment = false } = useConfig(
    "salon.appointment_worker_assignment_enabled"
  );
  const { setCalendarState } = useCalendarStateStore();

  // Automatically switch away from worker view when configuration is disabled
  useEffect(() => {
    if (viewType === "worker" && !showWorkerAssignment) {
      setCalendarState("day", currentDate);
    }
  }, [viewType, showWorkerAssignment, currentDate, setCalendarState]);

  const commonProps = {
    currentDate,
    onDateChange,
    onAppointmentClick,
    onAppointmentDrop,
  };

  return (
    <div className="h-full">
      {/* Render the appropriate view */}
      {viewType === "day" && <DayView {...commonProps} />}
      {viewType === "3-day" && <ThreeDayView {...commonProps} />}
      {viewType === "week" && <WeekView {...commonProps} />}
      {viewType === "month" && <MonthView {...commonProps} />}
      {viewType === "worker" && showWorkerAssignment && <WorkerView {...commonProps} />}
    </div>
  );
}
